import React, { useMemo, useState } from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { VendorCombobox } from '../VendorCombobox';
import {
  buildReviewHierarchy,
  formatSpecifications,
  parseSpecificationsInput,
  displayNotes,
  groupKey,
} from '../../utils/importReviewGrouping';
import {
  countChildren,
  hierarchyBreadcrumb,
  wouldCreateHierarchyCycle,
  type HierarchyNode,
} from '../../utils/modificationHierarchy';
import {
  importReviewCategoryDomId,
  importReviewGroupDomId,
} from '../../utils/importIntelligence';
import { AiSuggestedBadge } from './AiSuggestedBadge';
import type { ParsedModification } from './importReviewTypes';
import type { SmartSuggestionFields } from './importConstants';
import { clearAiSuggestion } from '../../utils/aiSuggestionFields';

export interface ReviewModification extends ParsedModification {}

export interface PendingCategoryApply {
  changedIndex: number;
  newCategory: string;
  similarIndices: number[];
}

interface ImportReviewGroupedListProps {
  modifications: ReviewModification[];
  excludedIndices: Set<number>;
  collapsedCategories: Set<string>;
  collapsedGroups: Set<string>;
  availableCategories: string[];
  availableVendors: string[];
  smartSuggestions: Record<number, SmartSuggestionFields>;
  selectedMods: Set<number>;
  showBulkSelect?: boolean;
  visibleIndices?: Set<number> | null;
  pendingCategoryApply?: PendingCategoryApply | null;
  onToggleCategory: (category: string) => void;
  onToggleGroup: (groupKey: string) => void;
  onToggleIncluded: (index: number) => void;
  onToggleSelect: (index: number) => void;
  onEdit: (index: number, field: keyof ReviewModification, value: unknown) => void;
  onCategoryChange: (index: number, newCategory: string, previousCategory: string) => void;
  onApplyCategoryToSimilar: (changedIndex: number, newCategory: string, similarIndices: number[]) => void;
  onDismissCategoryApply: () => void;
  onRemove: (index: number) => void;
}

export const ImportReviewGroupedList: React.FC<ImportReviewGroupedListProps> = ({
  modifications,
  excludedIndices,
  collapsedCategories,
  collapsedGroups,
  availableCategories,
  availableVendors,
  smartSuggestions,
  selectedMods,
  showBulkSelect = false,
  visibleIndices = null,
  pendingCategoryApply = null,
  onToggleCategory,
  onToggleGroup,
  onToggleIncluded,
  onToggleSelect,
  onEdit,
  onCategoryChange,
  onApplyCategoryToSimilar,
  onDismissCategoryApply,
  onRemove,
}) => {
  const hierarchy = useMemo(() => buildReviewHierarchy(modifications), [modifications]);
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());

  const categoryOptions = useMemo(
    () => availableCategories.map((cat) => ({ value: cat, label: cat })),
    [availableCategories]
  );

  const editField = (index: number, field: keyof ReviewModification, value: unknown) => {
    onEdit(index, field, value);
    if (
      field === 'category' ||
      field === 'vendor' ||
      field === 'description' ||
      field === 'specifications' ||
      field === 'group_label' ||
      field === 'parentComponent'
    ) {
      const mod = modifications[index];
      const nextSuggested = clearAiSuggestion(mod?.ai_suggested, field === 'parentComponent' ? 'group_label' : field);
      onEdit(index, 'ai_suggested', nextSuggested);
    }
  };

  const toggleItemExpanded = (index: number) => {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const inputClass = 'input';

  const isVisible = (index: number) => !visibleIndices || visibleIndices.has(index);

  const parentOptions = useMemo(
    () =>
      modifications.map((m, i) => ({
        index: i,
        temporary_id: m.temporary_id || `item_${i + 1}`,
        label: (m.title || `Item ${i + 1}`).trim(),
      })),
    [modifications]
  );

  const moveUnder = (index: number, parentIndex: number | null) => {
    if (parentIndex === null) {
      editField(index, 'parent_temporary_id', null);
      editField(index, 'parentComponent', '');
      editField(index, 'depth', 0);
      return;
    }
    if (wouldCreateHierarchyCycle(modifications, index, parentIndex)) {
      return;
    }
    const parent = modifications[parentIndex];
    const parentTemp = parent.temporary_id || `item_${parentIndex + 1}`;
    if (!parent.temporary_id) {
      editField(parentIndex, 'temporary_id', parentTemp);
    }
    const childTemp = modifications[index].temporary_id || `item_${index + 1}`;
    if (!modifications[index].temporary_id) {
      editField(index, 'temporary_id', childTemp);
    }
    editField(index, 'parent_temporary_id', parentTemp);
    editField(index, 'parentComponent', parent.title || '');
    editField(index, 'depth', (parent.depth || 0) + 1);
  };

  const renderItem = (index: number, depth = 0, childCount = 0) => {
    const mod = modifications[index];
    if (!mod || !isVisible(index)) return null;

    const included = !excludedIndices.has(index);
    const specsText = formatSpecifications(mod.specifications);
    const notesText = displayNotes(mod.notes);
    const isExpanded = expandedItems.has(index);
    const suggestions = smartSuggestions[index] || {};
    const vendorSuggestion = suggestions.vendor;
    const categorySuggestion = suggestions.category;
    const hasSuggestions = Boolean(vendorSuggestion || categorySuggestion || suggestions.cost);
    const showCategoryApply =
      pendingCategoryApply?.changedIndex === index && pendingCategoryApply.similarIndices.length > 0;
    const crumb = hierarchyBreadcrumb(modifications, index);
    const rawType = mod.item_type || (mod.is_group ? 'group' : 'part');
    const typeLabel =
      rawType === 'assembly' || rawType === 'group'
        ? 'Assembly'
        : rawType === 'section'
          ? 'Section'
          : 'Part';
    const pad = Math.min(depth, 4) * 10;
    const pathLabel = [mod.category, ...(crumb.length ? crumb : [])].filter(Boolean).join(' › ');
    const hasSuggestedFields = Boolean(
      mod.ai_suggested?.vendor ||
        mod.ai_suggested?.category ||
        mod.ai_suggested?.description ||
        mod.ai_suggested?.specifications ||
        mod.ai_suggested?.group_label
    );
    const needsLook =
      mod.category === 'Miscellaneous' ||
      (Boolean(mod.category_is_new) && !mod.category_confirmed) ||
      (typeof mod.confidence === 'number' && mod.confidence < 0.55);

    return (
      <li
        key={index}
        className={`rounded-lg border transition-colors ${
          !included
            ? 'border-white/[0.08] bg-[#141414]/40 opacity-60'
            : mod.duplicate_warning
              ? 'border-yellow-700/50 bg-yellow-900/10'
              : selectedMods.has(index)
                ? 'border-orange-600/40 bg-orange-900/10'
                : depth > 0
                  ? 'border-white/[0.08] bg-[#141414]/20'
                  : 'border-white/[0.10] bg-[#141414]/30'
        }`}
        style={{ marginLeft: pad }}
      >
        <div className="p-3 space-y-2">
          <div className="flex items-start gap-2.5">
            {showBulkSelect ? (
              <input
                type="checkbox"
                checked={selectedMods.has(index)}
                onChange={() => onToggleSelect(index)}
                title="Select item"
                aria-label={`Select ${mod.title || 'part'}`}
                className="mt-1.5 rounded border-white/[0.14] text-orange-500 focus:ring-orange-500 shrink-0 w-4 h-4"
              />
            ) : (
              <button
                type="button"
                role="checkbox"
                aria-checked={included}
                aria-label={included ? `Exclude ${mod.title || 'part'}` : `Include ${mod.title || 'part'}`}
                onClick={() => onToggleIncluded(index)}
                className={`mt-0.5 shrink-0 w-11 h-11 sm:w-9 sm:h-9 rounded-lg border flex items-center justify-center transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 ${
                  included
                    ? 'border-orange-500/70 bg-orange-900/30 text-orange-300'
                    : 'border-white/[0.10] bg-[#141414] text-gray-500'
                }`}
              >
                {included ? (
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20" aria-hidden>
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <span className="w-3 h-3 rounded-sm border border-white/[0.14]" aria-hidden />
                )}
              </button>
            )}

            <div className="flex-1 min-w-0 space-y-1.5">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2 text-[10px] text-gray-500 mb-0.5">
                    {depth > 0 && (
                      <span className="inline-block w-0.5 h-3 bg-orange-500/70 rounded-full" aria-hidden />
                    )}
                    <span className="uppercase tracking-wide">{typeLabel}</span>
                    {childCount > 0 && (
                      <span className="text-gray-400">{childCount} inside</span>
                    )}
                    {hasSuggestedFields && <AiSuggestedBadge show />}
                  </div>
                  <p className={`text-white font-medium break-words ${depth === 0 && (mod.is_group || mod.item_type === 'assembly') ? 'text-base' : 'text-sm'}`}>
                    {mod.title || 'Untitled part'}
                  </p>
                  <p className="text-xs text-gray-500 truncate" title={pathLabel}>
                    {pathLabel}
                    {mod.vendor?.trim() ? ` · ${mod.vendor.trim()}` : ''}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => toggleItemExpanded(index)}
                  className="shrink-0 text-xs text-gray-300 hover:text-white px-3 py-2 rounded-lg border border-white/[0.10] min-h-[44px] sm:min-h-[36px]"
                  aria-expanded={isExpanded}
                >
                  {isExpanded ? 'Done' : 'Edit'}
                </button>
              </div>

              {(!isExpanded || mod.duplicate_warning) && (
                <>
                  {mod.duplicate_warning && mod.duplicate_resolution !== 'separate' && (
                    <div className="flex items-start gap-1.5 text-xs text-yellow-300 bg-yellow-900/20 border border-yellow-700/40 rounded px-2 py-1.5">
                      <ExclamationTriangleIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
                      <span className="flex-1">
                        {mod.duplicate_in_batch
                          ? 'Possible duplicate within this list'
                          : 'Already in this build. Possible match'}
                      </span>
                      <button
                        type="button"
                        onClick={() => onToggleIncluded(index)}
                        className="text-xs px-2 min-h-[44px] rounded border border-yellow-700/50 text-yellow-200 shrink-0"
                      >
                        {included ? 'Exclude' : 'Include'}
                      </button>
                      {included && <button type="button" className="text-xs min-h-[44px] px-2 underline" onClick={() => editField(index, 'duplicate_resolution', 'separate')}>Keep separate</button>}
                    </div>
                  )}
                  {needsLook && !mod.duplicate_warning && (
                    <p className="text-xs text-amber-300/90 flex items-center gap-1">
                      <ExclamationTriangleIcon className="h-4 w-4 shrink-0" aria-hidden="true" /> Needs a look
                    </p>
                  )}
                  {specsText && (
                    <p className="text-xs text-gray-400 break-words line-clamp-2">
                      <span className="text-gray-500">Specs:</span> {specsText}
                    </p>
                  )}
                </>
              )}

              {isExpanded ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 border-t border-white/[0.07]">
                  <div className="sm:col-span-2">
                    <label htmlFor={`import-title-${index}`} className="text-xs text-gray-500 block mb-1">Part name</label>
                    <input
                      type="text"
                      id={`import-title-${index}`}
                      required aria-invalid={!mod.title.trim()}
                      value={mod.title}
                      onChange={(e) => editField(index, 'title', e.target.value)}
                      className={`${inputClass} font-medium`}
                      placeholder="Part name"
                    />
                  </div>
                  <div>
                    <div className="flex items-center gap-1.5 mb-1">
                      <label htmlFor={`import-category-${index}`} className="text-xs text-gray-500">Category</label>
                      <AiSuggestedBadge show={Boolean(mod.ai_suggested?.category)} />
                    </div>
                    <select id={`import-category-${index}`} className="input" value={mod.category} onChange={(event) => {
                        const value = event.target.value;
                        const previous = mod.category;
                        editField(index, 'category', value);
                        editField(index, 'category_confirmed', true);
                        editField(index, 'category_is_new', false);
                        if (value && value !== previous) {
                          onCategoryChange(index, value, previous);
                        }
                      }}
                    >
                      {!availableCategories.includes(mod.category) && <option value={mod.category}>{mod.category || 'Choose a category'}</option>}
                      {categoryOptions.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                    </select>
                    {showCategoryApply && pendingCategoryApply && (
                      <div className="mt-2 rounded-lg border border-orange-600/40 bg-orange-900/15 px-3 py-2 text-xs">
                        <p className="text-orange-200 mb-2">
                          Apply {pendingCategoryApply.newCategory} to{' '}
                          {pendingCategoryApply.similarIndices.length} similar part
                          {pendingCategoryApply.similarIndices.length === 1 ? '' : 's'}?
                        </p>
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={() =>
                              onApplyCategoryToSimilar(
                                pendingCategoryApply.changedIndex,
                                pendingCategoryApply.newCategory,
                                pendingCategoryApply.similarIndices
                              )
                            }
                            className="px-2.5 py-1.5 rounded bg-orange-600/80 text-white hover:bg-orange-600"
                          >
                            Apply to similar
                          </button>
                          <button
                            type="button"
                            onClick={onDismissCategoryApply}
                            className="px-2.5 py-1.5 rounded border border-white/[0.10] text-gray-300 hover:text-white"
                          >
                            Not now
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                  <div>
                    <div className="flex items-center gap-1.5 mb-1">
                      <label htmlFor={`import-brand-${index}`} className="text-xs text-gray-500">Brand</label>
                      <AiSuggestedBadge show={Boolean(mod.ai_suggested?.vendor)} />
                    </div>
                    <VendorCombobox
                      id={`import-brand-${index}`}
                      value={mod.vendor || ''}
                      onChange={(value) => editField(index, 'vendor', value)}
                      vendors={availableVendors}
                      className={`text-xs py-1.5 ${mod.ai_suggested?.vendor ? 'border-sky-500/40' : ''}`}
                      placeholder="Brand"
                    />
                  </div>
                  <div>
                    <div className="flex items-center gap-1.5 mb-1">
                      <label className="text-xs text-gray-500">Parent or group</label>
                      <AiSuggestedBadge show={Boolean(mod.ai_suggested?.group_label)} />
                    </div>
                    <select
                      aria-label="Parent or group"
                      value={
                        mod.parent_temporary_id
                          ? String(
                              parentOptions.find((p) => p.temporary_id === mod.parent_temporary_id)?.index ?? ''
                            )
                          : ''
                      }
                      onChange={(e) => {
                        const raw = e.target.value;
                        if (raw === '') moveUnder(index, null);
                        else moveUnder(index, parseInt(raw, 10));
                      }}
                      className={inputClass}
                    >
                      <option value="">Top level</option>
                      {parentOptions
                        .filter((p) => p.index !== index)
                        .map((p) => (
                          <option key={p.index} value={p.index}>
                            {p.label}
                          </option>
                        ))}
                    </select>
                    <input
                      type="text"
                      value={mod.group_label || ''}
                      onChange={(e) => {
                        editField(index, 'group_label', e.target.value);
                      }}
                      className={`${inputClass} mt-2`}
                      aria-label="Optional section label"
                      placeholder="Optional section label"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 block mb-1">
                      {mod.cost_source === 'estimated' ? 'Estimated cost (USD)' : 'Cost'}
                    </label>
                    <input
                      type="number" inputMode="decimal" min="0" step="0.01" aria-label="Cost"
                      value={mod.cost ?? ''}
                      onChange={(e) =>
                        editField(index, 'cost', e.target.value ? parseFloat(e.target.value) : undefined)
                      }
                      className={inputClass}
                      placeholder={mod.is_custom ? 'Custom work — enter actual cost' : 'Optional'}
                    />
                    {mod.cost_source === 'estimated' && mod.price_basis && (
                      <p className="mt-1 text-xs text-gray-500">{mod.price_basis}</p>
                    )}
                  </div>
                  <div className="sm:col-span-2">
                    <div className="flex items-center gap-1.5 mb-1">
                      <label className="text-xs text-gray-500">Description</label>
                      <AiSuggestedBadge show={Boolean(mod.ai_suggested?.description)} />
                    </div>
                    <textarea aria-label="Description"
                      value={mod.description || ''}
                      onChange={(e) => editField(index, 'description', e.target.value)}
                      rows={2}
                      className={`${inputClass} resize-y ${mod.ai_suggested?.description ? 'border-sky-500/40' : ''}`}
                      placeholder="Short description (optional)"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <div className="flex items-center gap-1.5 mb-1">
                      <label className="text-xs text-gray-500">Specifications</label>
                      <AiSuggestedBadge show={Boolean(mod.ai_suggested?.specifications)} />
                    </div>
                    <textarea aria-label="Specifications"
                      value={specsText}
                      onChange={(e) =>
                        editField(index, 'specifications', parseSpecificationsInput(e.target.value))
                      }
                      rows={2}
                      className={`${inputClass} resize-y ${mod.ai_suggested?.specifications ? 'border-sky-500/40' : ''}`}
                      placeholder={"Final drive: 4.308\nRear differential: 3.309"}
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="text-xs text-gray-500 block mb-1">Notes</label>
                    <textarea aria-label="Notes"
                      value={notesText}
                      onChange={(e) => editField(index, 'notes', e.target.value)}
                      rows={2}
                      className={`${inputClass} resize-y`}
                    />
                  </div>
                  <div className="sm:col-span-2 flex flex-wrap items-center gap-2">
                    <label className="inline-flex items-center gap-2 text-sm text-gray-300 min-h-[44px]">
                      <input
                        type="checkbox"
                        checked={included}
                        onChange={() => onToggleIncluded(index)}
                        className="rounded border-white/[0.14] text-orange-500 focus:ring-orange-500"
                      />
                      Include in this import
                    </label>
                    <button
                      type="button"
                      onClick={() => onRemove(index)}
                      className="text-xs text-red-400 hover:text-red-300 px-2.5 py-2"
                    >
                      Remove from list
                    </button>
                  </div>
                  {hasSuggestions ? (
                    <div className="sm:col-span-2 flex flex-wrap gap-2 text-xs">
                      {vendorSuggestion && !mod.vendor ? (
                        <button
                          type="button"
                          onClick={() => editField(index, 'vendor', vendorSuggestion)}
                          className="text-sky-300 hover:text-sky-200 px-2 py-1 rounded border border-sky-500/30"
                        >
                          Use suggested brand: {vendorSuggestion}
                        </button>
                      ) : null}
                      {categorySuggestion && mod.category === 'Miscellaneous' ? (
                        <button
                          type="button"
                          onClick={() => {
                            editField(index, 'category', categorySuggestion);
                            editField(index, 'category_confirmed', true);
                          }}
                          className="text-sky-300 hover:text-sky-200 px-2 py-1 rounded border border-sky-500/30"
                        >
                          Use suggested category: {categorySuggestion}
                        </button>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </li>
    );
  };

  const filteredHierarchy = useMemo(() => {
    if (!visibleIndices) return hierarchy;

    const filterTree = (
      node: HierarchyNode<ParsedModification>
    ): HierarchyNode<ParsedModification> | null => {
      const children = node.children
        .map((child) => filterTree(child))
        .filter((c): c is HierarchyNode<ParsedModification> => c !== null);
      const selfVisible = visibleIndices.has(node.index);
      if (!selfVisible && children.length === 0) return null;
      return { ...node, children };
    };

    return hierarchy
      .map((cat) => {
        if (cat.recursive && cat.roots) {
          const roots = cat.roots
            .map((r) => filterTree(r as HierarchyNode<ParsedModification>))
            .filter((r): r is HierarchyNode<ParsedModification> => r !== null);
          const countNodes = (n: HierarchyNode<ParsedModification>): number =>
            1 + n.children.reduce((s, c) => s + countNodes(c), 0);
          const itemCount = roots.reduce((s, r) => s + countNodes(r), 0);
          return { ...cat, roots, itemCount, groups: cat.groups };
        }
        const filteredGroups = cat.groups
          .map((group) => ({
            ...group,
            items: group.items.filter(({ index }) => visibleIndices.has(index)),
          }))
          .filter((g) => g.items.length > 0);
        const count = filteredGroups.reduce((sum, g) => sum + g.items.length, 0);
        return { ...cat, groups: filteredGroups, itemCount: count };
      })
      .filter((c) => c.itemCount > 0);
  }, [hierarchy, visibleIndices]);

  const renderTreeNode = (node: HierarchyNode<ParsedModification>, depth: number) => {
    const nodeKey = `node:${node.index}`;
    const collapsed = collapsedGroups.has(nodeKey);
    const childTotal = countChildren(node);
    return (
      <div key={node.index} className="space-y-2">
        <div className="flex items-start gap-1">
          {node.children.length > 0 ? (
            <button
              type="button"
              onClick={() => onToggleGroup(nodeKey)}
              className="mt-3 text-gray-500 hover:text-white shrink-0 p-1"
              aria-label={collapsed ? 'Expand children' : 'Collapse children'}
            >
              <svg
                className={`w-3 h-3 transition-transform ${collapsed ? '' : 'rotate-90'}`}
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          ) : (
            <span className="w-5 shrink-0" />
          )}
          <ul className="flex-1 min-w-0 space-y-2">
            {renderItem(node.index, depth, childTotal)}
          </ul>
        </div>
        {!collapsed && node.children.length > 0 && (
          <div className="ml-2 sm:ml-3 border-l border-white/[0.10] pl-2 sm:pl-3 space-y-2">
            {node.children.map((child) => renderTreeNode(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  if (filteredHierarchy.length === 0) {
    return (
      <div className="text-center py-8 text-gray-400 rounded-lg border border-white/[0.07]">
        <p className="text-sm">No parts match your current filter.</p>
        <p className="text-xs mt-1">Try clearing search or switching to “Show all”.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {filteredHierarchy.map((cat) => {
        const { category, groups, itemCount, recursive, roots } = cat;
        const categoryCollapsed = collapsedCategories.has(category);

        return (
          <section
            key={category}
            id={importReviewCategoryDomId(category)}
            className="rounded-lg border border-white/[0.10] bg-[#161616]/40 overflow-hidden scroll-mt-4"
          >
            <button
              type="button"
              onClick={() => onToggleCategory(category)}
              className="w-full flex items-center justify-between gap-3 px-4 py-3 bg-[#141414]/80 hover:bg-white/[0.04] text-left min-h-[44px]"
              aria-expanded={!categoryCollapsed}
              aria-label={`${categoryCollapsed ? 'Expand' : 'Collapse'} ${category}`}
            >
              <div className="flex items-center gap-2 min-w-0">
                <svg
                  className={`w-4 h-4 text-gray-400 transition-transform shrink-0 ${categoryCollapsed ? '' : 'rotate-90'}`}
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="font-semibold text-white truncate">{category}</span>
              </div>
              <span className="text-xs text-gray-400 shrink-0">
                {itemCount} part{itemCount === 1 ? '' : 's'}
              </span>
            </button>

            {!categoryCollapsed && (
              <div className="p-3 space-y-4">
                {recursive && roots ? (
                  <div className="space-y-3">
                    {roots.map((root) =>
                      renderTreeNode(root as HierarchyNode<ParsedModification>, 0)
                    )}
                  </div>
                ) : (
                  groups.map((group) => {
                    const gKey = groupKey(category, group.label);
                    const groupCollapsed = collapsedGroups.has(gKey);
                    const showGroupHeader = group.label !== 'General' || groups.length > 1;

                    return (
                      <div key={gKey} id={importReviewGroupDomId(gKey)} className="scroll-mt-4">
                        {showGroupHeader && (
                          <button
                            type="button"
                            onClick={() => onToggleGroup(gKey)}
                            className="w-full flex items-center justify-between gap-2 mb-2 px-2 py-2 rounded hover:bg-[#161616]/50 text-left min-h-[44px]"
                          >
                            <div className="flex items-center gap-2 min-w-0">
                              <svg
                                className={`w-3 h-3 text-gray-500 transition-transform shrink-0 ${groupCollapsed ? '' : 'rotate-90'}`}
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                                  clipRule="evenodd"
                                />
                              </svg>
                              <span className="text-sm font-medium text-orange-200/90 truncate">
                                {group.label}
                              </span>
                            </div>
                            <span className="text-xs text-gray-500 shrink-0">
                              {group.items.length} part{group.items.length === 1 ? '' : 's'}
                            </span>
                          </button>
                        )}

                        {(!showGroupHeader || !groupCollapsed) && (
                          <ul
                            className={`space-y-2 ${showGroupHeader ? 'pl-2 sm:pl-4 border-l-2 border-white/[0.10]' : ''}`}
                          >
                            {group.items.map(({ index }) => renderItem(index))}
                          </ul>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
};
